import { AbstractControl, FormArray, FormGroup } from '@angular/forms';

/**
 * Marks every control in the form (including nested FormGroups/FormArrays) as
 * touched + dirty and re-runs validity, so `<adp-input>` / Material form
 * fields surface their validation messages on a submit attempt — parity with
 * a native HTML form submit, which reactive forms don't give you for free.
 */
export function validateAllFormFields(form: FormGroup | FormArray): void {
  Object.values(form.controls).forEach((control: AbstractControl) => {
    if (control instanceof FormGroup || control instanceof FormArray) {
      validateAllFormFields(control);
    } else {
      control.markAsTouched({ onlySelf: true });
      control.markAsDirty({ onlySelf: true });
      control.updateValueAndValidity({ onlySelf: true });
    }
  });
}

/**
 * Focuses the first invalid control's rendered element so keyboard and
 * screen-reader users land on the first thing they need to fix after a
 * failed submit attempt. Matches on the `formcontrolname` attribute (case
 * folded by the DOM); pass `containerSelector` to scope the lookup when a
 * page has more than one form using the same control names.
 *
 * The `[formcontrolname]` match is often a custom-element host (`<adp-input>`,
 * `<adp-select>`), which isn't natively focusable — focusing it directly is a
 * no-op for every field except a raw `<input formControlName>`. Drill into the
 * matched element for a real focusable descendant before falling back to the
 * host itself.
 */
export function focusFirstInvalid(form: FormGroup | FormArray, containerSelector?: string): void {
  const invalidControlName = Object.keys(form.controls).find((name) => form.get(name)?.invalid);
  if (!invalidControlName) {
    return;
  }

  const root: ParentNode = containerSelector ? (document.querySelector(containerSelector) ?? document) : document;
  const element = root.querySelector<HTMLElement>(`[formcontrolname="${invalidControlName}"]`);
  const focusTarget = element?.querySelector<HTMLElement>('input, select, textarea') ?? element;
  focusTarget?.focus();
}
